import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useAudio } from "../contexts/AudioContext";
import { Search, Play, Pause, ListMusic, Music } from "lucide-react";

const KINDS = ["aarti", "bhajan", "mantra", "stotram", "chalisa"];

export const toAudioItem = (g, t) => ({ id: null, deity: g.slug, track: t.id, audio_url: t.audio_url, sanskrit: t.title, english: g.name, meaning: t.kind });

export default function Bhajans() {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const { playQueue, playing, currentName, stop } = useAudio();
  useSeo("Bhajans, Aartis & Mantras");
  useEffect(() => { api.get("/bhajans").then((r) => setData(r.data)); }, []);

  const groups = useMemo(() => (data?.groups || []).map((g) => ({
    ...g, tracks: g.tracks.filter((t) => (!kind || t.kind === kind) && (!q || `${t.title} ${g.name}`.toLowerCase().includes(q.toLowerCase()))),
  })).filter((g) => g.tracks.length), [data, kind, q]);
  const all = groups.flatMap((g) => g.tracks.map((t) => toAudioItem(g, t)));

  return (
    <div data-testid="bhajans-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-2xl">
          <div className="overline">॥ भजन संग्रह ॥</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">Bhajans & Aartis</h1>
          <p className="text-[#63534E] mt-5 text-base md:text-lg">{data?.total_tracks ?? 0} sacred recordings sorted by deity. Press "Play all" and the music keeps going, one track after another.</p>
        </div>
        {all.length > 0 && <button data-testid="bhajans-play-everything" onClick={() => playQueue(all)} className="btn-saffron"><ListMusic className="w-4 h-4" /> Play everything</button>}
      </div>

      <div className="mt-10 flex flex-col md:flex-row gap-4 md:items-center">
        <div className="relative md:w-72">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#8C6D3B]" />
          <input data-testid="bhajan-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a bhajan or deity…" className="w-full h-11 pl-10 pr-4 rounded-full bg-[#FFFDF9] border border-[#D4AF37]/40 text-sm focus:outline-none focus:border-[#4A0E17]" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["", ...KINDS].map((k) => (
            <button key={k || "all"} data-testid={`bhajan-kind-${k || "all"}`} onClick={() => setKind(k)}
              className={`px-4 h-9 rounded-full text-sm border capitalize transition-sacred ${kind === k ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-[#FFFDF9] text-[#4A0E17] border-[#D4AF37]/40"}`}>{k || "All"}</button>
          ))}
        </div>
      </div>

      {data && groups.length === 0 && (
        <div data-testid="bhajans-empty" className="mt-12 bg-[#FFFDF9] sacred-border rounded-3xl p-16 text-center">
          <Music className="w-12 h-12 text-[#D4AF37] mx-auto" />
          <h3 className="font-serif text-2xl text-[#4A0E17] mt-4">{data.total_tracks ? "No tracks match your filter" : "Recordings are being added"}</h3>
          <p className="text-[#63534E] mt-2">Meanwhile, chant along on any <Link to="/deities" className="text-[#E65100]">deity page</Link>.</p>
        </div>
      )}

      <div className="mt-10 space-y-8">
        {groups.map((g) => (
          <section key={g.slug} data-testid={`bhajan-group-${g.slug}`} className="bg-[#FFFDF9] sacred-border rounded-3xl overflow-hidden">
            <div className="flex items-center gap-4 p-5 border-b border-[#D4AF37]/30">
              <img src={g.image_url} alt={g.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#D4AF37]" />
              <div className="flex-1 min-w-0">
                <div className="font-devanagari text-sm text-[#8C6D3B]">{g.sanskrit}</div>
                <Link to={`/deities/${g.slug}`} className="font-serif text-2xl text-[#4A0E17] hover:text-[#E65100]">{g.name}</Link>
                <div className="text-xs text-[#63534E]">{g.tracks.length} track{g.tracks.length > 1 ? "s" : ""}</div>
              </div>
              <button data-testid={`bhajan-playall-${g.slug}`} onClick={() => playQueue(g.tracks.map((t) => toAudioItem(g, t)))} className="btn-ghost-gold text-sm"><Play className="w-4 h-4" /> Play all</button>
            </div>
            <div className="divide-y divide-[#D4AF37]/20">
              {g.tracks.map((t, i) => {
                const on = playing && currentName?.track === t.id;
                return (
                  <button key={t.id} data-testid={`bhajan-track-${t.id}`} onClick={() => (on ? stop() : playQueue(g.tracks.map((x) => toAudioItem(g, x)), i))}
                    className={`w-full flex items-center gap-4 px-5 py-3 text-left transition-sacred ${on ? "bg-[#F3E5AB]/50" : "hover:bg-[#F3E5AB]/25"}`}>
                    <span className="w-6 text-right font-mono text-xs text-[#8C6D3B]">{i + 1}</span>
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center ${on ? "bg-[#E65100] text-white" : "bg-[#4A0E17] text-[#D4AF37]"}`}>{on ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</span>
                    <span className="flex-1 font-semibold text-[#4A0E17]">{t.title}</span>
                    <span className="text-xs capitalize bg-[#F3E5AB]/60 border border-[#D4AF37]/30 rounded-full px-2 py-0.5">{t.kind}</span>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
